import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMapStore } from '../../stores/mapStore.js';
import type { AnnotationType, MapAnnotation } from '../../types/index.js';

// ─── Squircle icon helper (P / H) ─────────────────────────────────────────────
function squircleEl(letter: string, bg: string): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = `width:26px;height:26px;background:${bg};border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:900;color:white;font-family:system-ui,sans-serif;box-shadow:0 1px 4px rgba(0,0,0,0.35);`;
  el.textContent = letter;
  return el;
}

// ─── Emoji icon helper ─────────────────────────────────────────────────────────
function emojiEl(emoji: string): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = 'font-size:22px;line-height:1;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.4));';
  el.textContent = emoji;
  return el;
}

// ─── Text annotation helper ────────────────────────────────────────────────────
function textEl(label: string): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = 'background:white;border:1.5px solid #64748b;border-radius:5px;padding:2px 7px;font-size:12px;font-family:system-ui,sans-serif;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,0.2);font-weight:600;color:#1e293b;';
  el.textContent = label;
  return el;
}

function createAnnotationEl(annotation: MapAnnotation): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'annotation-marker';
  wrapper.style.cursor = 'pointer';
  wrapper.title = 'Double-click to remove';

  let inner: HTMLElement;
  switch (annotation.type) {
    case 'star':    inner = emojiEl('⭐'); break;
    case 'trailer': inner = emojiEl('🚐'); break;
    case 'heart':   inner = emojiEl('❤️'); break;
    case 'camera':  inner = emojiEl('📷'); break;
    case 'parking': inner = squircleEl('P', '#2563eb'); break;
    case 'holding': inner = squircleEl('H', '#0d9488'); break;
    case 'lunch':   inner = emojiEl('🍽️'); break;
    case 'text':    inner = textEl(annotation.label ?? ''); break;
    default:        inner = emojiEl('📍');
  }

  wrapper.appendChild(inner);
  return wrapper;
}

interface AnnotationLayerProps {
  map: mapboxgl.Map;
}

export function AnnotationLayer({ map }: AnnotationLayerProps) {
  const { annotations, removeAnnotation } = useMapStore();
  const markerMapRef = useRef<Map<string, mapboxgl.Marker>>(new Map());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      markerMapRef.current.forEach(m => m.remove());
      markerMapRef.current.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Incremental add / remove as annotation list changes
  useEffect(() => {
    const ids = new Set(annotations.map(a => a.id));

    // Remove stale markers
    markerMapRef.current.forEach((marker, id) => {
      if (!ids.has(id)) {
        marker.remove();
        markerMapRef.current.delete(id);
      }
    });

    // Add new markers
    annotations.forEach(annotation => {
      if (!markerMapRef.current.has(annotation.id)) {
        const el = createAnnotationEl(annotation);
        el.addEventListener('dblclick', (e) => {
          e.stopPropagation();
          removeAnnotation(annotation.id);
        });
        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([annotation.lng, annotation.lat])
          .addTo(map);
        markerMapRef.current.set(annotation.id, marker);
      }
    });
  }, [annotations, map, removeAnnotation]);

  return null;
}

// ─── Annotation type config used by DrawingTools ─────────────────────────────
export const ANNOTATION_TOOLS: { type: AnnotationType; label: string; icon: string; squircle?: string }[] = [
  { type: 'star',    label: 'Star (scene)',    icon: '⭐' },
  { type: 'trailer', label: 'Basecamp trailer', icon: '🚐' },
  { type: 'heart',   label: 'Favourite',        icon: '❤️' },
  { type: 'camera',  label: 'Camera position',  icon: '📷' },
  { type: 'parking', label: 'Parking',          icon: 'P', squircle: '#2563eb' },
  { type: 'holding', label: 'Holding',          icon: 'H', squircle: '#0d9488' },
  { type: 'lunch',   label: 'Lunch / catering', icon: '🍽️' },
  { type: 'text',    label: 'Text label',       icon: 'T' },
];
